import { useState } from 'react';
import { Check, ChevronDown, Filter, ListFilter } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/shared/components/ui/popover';
import { cn } from '@/shared/lib/cn';

export type InventarioTipo = 'Hornos' | 'Accesorios' | 'Repuestos';

export const INVENTARIO_TIPOS: InventarioTipo[] = ['Hornos', 'Accesorios', 'Repuestos'];

export type InventarioFilterValue = {
  lowStockOnly: boolean;
  conPorIngresar: boolean;
  /** Allowed `TipoInventario_I` values. An empty set means "no tipo
   *  filter" (everything passes); otherwise only rows whose tipo is
   *  in the set survive. */
  tipos: Set<InventarioTipo>;
};

export const DEFAULT_INVENTARIO_FILTER: InventarioFilterValue = {
  lowStockOnly: false,
  conPorIngresar: false,
  tipos: new Set(),
};

export function inventarioFilterActive(v: InventarioFilterValue): boolean {
  return v.lowStockOnly || v.conPorIngresar || v.tipos.size > 0;
}

export function FiltrarPopover({
  value,
  onChange,
  onReset,
  showEmpty,
  onShowEmptyChange,
}: {
  value: InventarioFilterValue;
  onChange: (next: InventarioFilterValue) => void;
  onReset: () => void;
  /**
   * Optional "Mostrar todas las unidades" toggle. When provided, an
   * extra entry surfaces inside the popover on mobile only — the
   * desktop toolbar already exposes the same toggle inline, so we
   * don't duplicate it there.
   */
  showEmpty?: boolean;
  onShowEmptyChange?: (next: boolean) => void;
}) {
  const [open, setOpen] = useState(false);
  const active = inventarioFilterActive(value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={cn('h-12 gap-2', active && 'border-foreground/60')}
        >
          <Filter className="h-4 w-4" aria-hidden />
          Filtrar
          {active && <span className="ml-1 inline-flex h-1.5 w-1.5 rounded-full bg-emerald-500" />}
          <ChevronDown className="h-4 w-4 opacity-60" aria-hidden />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-72 space-y-3 p-3">
        {onShowEmptyChange && (
          <div className="space-y-1.5 sm:hidden">
            <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <ListFilter className="h-3.5 w-3.5" aria-hidden /> Vista
            </p>
            <FilterToggle
              label="Mostrar todas las unidades"
              checked={showEmpty ?? false}
              onSelect={() => onShowEmptyChange(!(showEmpty ?? false))}
            />
          </div>
        )}
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Tipo
          </p>
          <div className="flex flex-col gap-1">
            {INVENTARIO_TIPOS.map((tipo) => (
              <FilterToggle
                key={tipo}
                label={tipo}
                checked={value.tipos.has(tipo)}
                onSelect={() => {
                  const next = new Set(value.tipos);
                  if (next.has(tipo)) next.delete(tipo);
                  else next.add(tipo);
                  onChange({ ...value, tipos: next });
                }}
              />
            ))}
          </div>
        </div>
        <div className="space-y-1.5">
          <p className="flex items-center gap-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
            <ListFilter className="h-3.5 w-3.5" aria-hidden /> Stock
          </p>
          <div className="flex flex-col gap-1">
            <FilterToggle
              label="Solo sin disponible"
              checked={value.lowStockOnly}
              onSelect={() => onChange({ ...value, lowStockOnly: !value.lowStockOnly })}
            />
            <FilterToggle
              label="Con por ingresar"
              checked={value.conPorIngresar}
              onSelect={() => onChange({ ...value, conPorIngresar: !value.conPorIngresar })}
            />
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <Button size="sm" variant="ghost" onClick={onReset}>
            Reiniciar
          </Button>
          <Button size="sm" onClick={() => setOpen(false)}>
            Listo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function FilterToggle({
  label,
  checked,
  onSelect,
}: {
  label: string;
  checked: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={cn(
        'flex items-center justify-between rounded-md border px-2.5 py-1.5 text-sm transition-colors',
        checked
          ? 'border-foreground bg-foreground/5 text-foreground'
          : 'border-border text-muted-foreground hover:bg-secondary',
      )}
    >
      <span>{label}</span>
      {checked && <Check className="h-4 w-4 text-emerald-600" aria-hidden />}
    </button>
  );
}
